import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPermits, apiSites } from '@/lib/api';
import { PERMIT_TYPES, STATUS_OPTIONS, statusLabel, isAdminNational } from '@/constants/permits';
import { PERMITS } from '@/constants/testIds';
import { useAuth } from '@/contexts/AuthContext';
import ExportImportButtons from '@/components/ExportImportButtons';
import { Search, FilePlus, FileText, Calendar } from 'lucide-react';

export default function PermitsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminNational(user);
  const [permits, setPermits] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', site: '', status: '', mine: false });
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.site) params.site = filters.site;
      if (filters.status) params.status = filters.status;
      if (filters.mine) params.mine = true;
      const { data } = await apiPermits.list(params);
      setPermits(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    apiSites.list().then(r => setSites(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-line */ }, [filters]);

  const filtered = permits.filter((p) =>
    !search ||
    p.numero?.toLowerCase().includes(search.toLowerCase()) ||
    p.titre?.toLowerCase().includes(search.toLowerCase()) ||
    p.lieu?.toLowerCase().includes(search.toLowerCase())
  );

  const typeLabel = (id) => PERMIT_TYPES.find((t) => t.id === id)?.label || id;

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">
            Registre
          </div>
          <h1 className="text-4xl font-display font-black tracking-tighter text-[#09090B]">
            Autorisations de Travail
          </h1>
        </div>
        <div className="inline-flex items-center gap-3">
          <ExportImportButtons
            exportPath="/admin/export/permits.csv"
            importPath="/admin/import/permits"
            filenameHint="autorisations_permis_lafarge.csv"
            visible={isAdmin}
            onImported={() => load()}
          />
          <button
            data-testid={PERMITS.newButton}
            onClick={() => navigate('/permits/new')}
            className="btn-industrial-primary flex items-center gap-2"
          >
            <FilePlus className="w-4 h-4" /> Nouvelle ADT
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="section-card">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Rechercher par n°, titre, lieu..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-industrial w-full pl-11"
              data-testid="permits-search"
            />
          </div>
          <select
            data-testid={PERMITS.filterType}
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="input-industrial"
          >
            <option value="">Tous les types</option>
            {PERMIT_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <select
            data-testid={PERMITS.filterSite}
            value={filters.site}
            onChange={(e) => setFilters({ ...filters, site: e.target.value })}
            className="input-industrial"
          >
            <option value="">Tous les sites</option>
            {sites.map((s) => (
              <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
            ))}
          </select>
          <select
            data-testid={PERMITS.filterStatus}
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="input-industrial"
          >
            <option value="">Tous les statuts</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="mt-3 flex items-center">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              data-testid={PERMITS.filterMine}
              type="checkbox"
              checked={filters.mine}
              onChange={(e) => setFilters({ ...filters, mine: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="font-semibold text-[#09090B]">Uniquement mes permis</span>
          </label>
        </div>
      </div>

      {/* List */}
      <div data-testid={PERMITS.list} className="section-card p-0 overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-stone-500">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div data-testid={PERMITS.emptyState} className="text-center py-20">
            <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <div className="text-base font-bold uppercase tracking-wider text-stone-600 mb-2">
              Aucun permis trouvé
            </div>
            <p className="text-sm text-stone-500 mb-6">Ajustez vos filtres ou créez un nouveau permis.</p>
            <button onClick={() => navigate('/permits/new')} className="btn-industrial-primary">
              Créer un permis
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-stone-50 border-b-2 border-stone-200">
                <th className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600 px-6 py-3">N°</th>
                <th className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600 px-6 py-3">Titre</th>
                <th className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600 px-6 py-3">Type</th>
                <th className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600 px-6 py-3">Site</th>
                <th className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600 px-6 py-3">Période</th>
                <th className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600 px-6 py-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  data-testid={PERMITS.row(p.id)}
                  onClick={() => navigate(`/permits/${p.id}`)}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 font-mono text-xs text-stone-700">{p.numero}</td>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-[#09090B] text-sm">{p.titre}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{p.lieu}</div>
                  </td>
                  <td className="px-6 py-4 text-xs text-stone-700 font-semibold">{typeLabel(p.type)}</td>
                  <td className="px-6 py-4 text-xs text-stone-700">{p.site_code}</td>
                  <td className="px-6 py-4 text-xs text-stone-700">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {p.date_debut}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`badge-status badge-${p.status}`}>{statusLabel(p.status)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
